'use client'

import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import { PageTitleContext } from "@/context/pageTitleContext"
import { BannerContext } from "@/context/bannerContext"
import { AuthContext } from "@/context/authContext"
import { DeviceContext } from "@/context/deviceContext"
import Tabs from "@/components/etc/tab2"
import PageBanner from "@/components_mobile/banner/page_banner"
import Search from "@/app/event/_search"
import api from "@/util/api"
import getQueryString from "@/util/queryString"
import {
    searchQueryStringTypes,
    searchQueryStringTypesDefault,
    BoardViewTypes,
} from "@/type/list"
import { tabsType } from "@/type/page"
import { usePathname, useSearchParams } from "next/navigation"
import customAlert from "@/util/customAlert"
import { useTranslations } from "next-intl"

export const CommonListContext = createContext<{
    list: any[]
    selectContent: any
    setContent: (id: number) => void
    editCategory: number
    post?: string
    fetchData?: () => void
    isFirstRender: boolean
    setIsFirstRender: React.Dispatch<React.SetStateAction<boolean>>
    loading: boolean
}>({
    list: [],
    selectContent: undefined,
    setContent: () => {},
    editCategory: 2,
    post: undefined,
    fetchData: () => {},
    isFirstRender: true,
    setIsFirstRender: () => {},
    loading: false,
})

// ======================================================================================
// Types
// ======================================================================================
interface TabWithParam extends tabsType {
    param: Record<string, any>
}

interface CommonConfigProps {
    children: ReactNode
    apiUrlBase: string
    responseKey: string
    defaultCate: number
    tabs_path: TabWithParam[]
    editCategories?: number[]
    ViewComponent: React.ComponentType<any>
    isSearch?: boolean
    perPage: number
}




export default function CommonConfigInfinite({
    children,
    apiUrlBase,
    responseKey,
    defaultCate,
    tabs_path,
    editCategories = [],
    ViewComponent,
    isSearch,
    perPage,
}: CommonConfigProps) {

    const { pageTitle, SetPageTitle } = useContext(PageTitleContext)
    const { page_top_banner } = useContext(BannerContext)
    const { user } = useContext(AuthContext)
    const { mobileMode } = useContext(DeviceContext)

    const pathName = usePathname()
    const searchParams = useSearchParams()



    const [list, setList] = useState<any[]>([])
    const [selectContent, setSelectContent] = useState<BoardViewTypes>()
    const [cachedContents, setCachedContents] = useState<Record<number, any>>({})
    const [loading, setLoading] = useState(false)

    const [queryString, setQueryString] = useState<searchQueryStringTypes>()
    const [totalCount, setTotalCount] = useState(0)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const [editCategory, setEditCategory] = useState(defaultCate)
    const [editAuth, setEditAuth] = useState(false)
    const [writeAuth, setWriteAuth] = useState(false)

    const [isFirstRender, setIsFirstRender] = useState(true)
    
    const [isuserSearch, setIsuserSearch] = useState<boolean>(false)


    const [isInitialLoad, setIsInitialLoad] = useState(true) // 처음 한 번만 처리
    const [isReplacingUrl, setIsReplacingUrl] = useState(false) // replaceState 무한 루프 방지
    const [ignoreId, setIgnoreId] = useState(false) // 초기 진입 이후 id 무시

    const loadMoreRef = useRef<HTMLDivElement>(null)

    const isTest = true;

    const a = useTranslations("Sentence")

    // ======================================================================================
    // 페이지 이동없이 URL만 업데이트
    // ======================================================================================
    function replaceQueryWithoutNav(pathname: string, qs: URLSearchParams) {
        const newUrl = `${pathname}?${qs.toString()}`
        const currentUrl = window.location.pathname + window.location.search

        if (newUrl !== currentUrl) {
            // window.history.replaceState(null, "", newUrl)
            window.history.pushState(null, "", newUrl)
        }
    }
    function safeReplace(qs: URLSearchParams) {
        setIsReplacingUrl(true)
        replaceQueryWithoutNav(pathName, qs)
        setTimeout(() => setIsReplacingUrl(false), 30)
    }

    // ======================================================================================
    // 페이지 로드
    // ======================================================================================
    const fetchAbortRef = useRef<AbortController | null>(null);
    async function fetchListPage(_page: number) {
        if (!queryString) return
        if (!hasMore && !isuserSearch) return

        if(isTest) console.log("**** fetchListPage page -->", page)
        
        // 중복 요청 중단
        if (fetchAbortRef.current) {
            fetchAbortRef.current.abort();
        }
        // 새로 생성
        const controller = new AbortController();
        fetchAbortRef.current = controller;

        try {

            setLoading(true)

            const params = { ...queryString, page: _page }
            const apiUrl = apiUrlBase + getQueryString(params)

            const res = await api(apiUrl, { signal: controller.signal })
            const { data } = await res.json()

            if (controller.signal.aborted) return;


            const newList = data[responseKey] || []
            const newTotal = data.total_count ?? 0

            setTotalCount(newTotal)

            // 중복 제거
            setList(prev => {
                const existing = new Set(prev.map(v => v.id))
                const filtered = newList.filter((v: { id: number }) => !existing.has(v.id))
                return [...prev, ...filtered]
            })

            if (!newList.length || list.length + newList.length >= newTotal) {
                setHasMore(false)
            }
        }  catch (error: any) {
            if (error.name === "AbortError") return;
            console.error("EEEEEEEEERROR!!  : ", error);


            const status = error.status ?? error.response?.status;
            if (status === 500) {
                if(isTest) console.log("**** 500 DETECTED: hasMore = false 로 변경해서 무한 요청 방지");
                setHasMore(false); // 더 이상 페이지 로드 못하게
                setLoading(false); // 로딩 종료 (무한후킹 방지)
                // customAlert(`페이지 로드에 실패하였습니다. 관리자에세 문의 바랍니다. (${status})`, "warning")
                customAlert(a("InvalidPage"), "warning")
                return; 
            }

        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
                setIsuserSearch(false)
            }
        }

        setLoading(false)
    }

    
    // ======================================================================================
    // 현재까지 불러온 페이지 모두 리로드
    // ======================================================================================
    async function reloadUntilCurrentPage(id?:number) {
        if (!queryString) return
        if (isScanning) return
        if (isInitialLoad) return

        // 기존요청 중단
        if (fetchAbortRef.current) {
            fetchAbortRef.current.abort()
        }

        setLoading(true)

        // 현재 페이지 파악
        const loadedPage = Math.ceil(list.length / (queryString.page_count || perPage))

        if(isTest) console.log("**** reloadUntilCurrentPage",selectContent, loadedPage,id)

        let merged: any[] = []
        const seenIds = new Set<number>()

        // 재요청
        for (let p = 1; p <= loadedPage; p++) {
            const q = { ...queryString, page: p }
            const apiUrl = apiUrlBase + getQueryString(q)

            try {
                const res = await api(apiUrl)
                const { data } = await res.json()

                const listData = data[responseKey] || []

                const filtered = listData.filter((item: any) => {
                    if (seenIds.has(item.id)) return false
                    seenIds.add(item.id)
                    return true
                })

                merged = [...merged,...filtered]

            } catch (err) {
                console.error(err)
            }
        }
        setList(merged)
        setHasMore(true)
        setLoading(false)

        // 상세 선택요소 있으면 보이기 
        if (selectContent?.id || id) {
            const newId = selectContent?.id ? selectContent.id : id;
            const updated = merged.find(el => el.id === newId)
            setSelectContent(updated)
        }
    }



    // ======================================================================================
    // 매개변수 포함 시 
    // ======================================================================================
    async function fetchSingle(id: number) {
        let page = 1
        let found = null

        while (!found) {
            const tmp = await fetchListPageReturn(page)
            found = tmp.find((el: any) => el.id === id)

            if (found) return found
            if (!tmp.length) break

            page++
        }
        return null
    }
    async function fetchListPageReturn(_page: number) {
        if (!queryString) return []

        const params = { ...queryString, page: _page }
        const apiUrl = apiUrlBase + getQueryString(params)

        try {
            const res = await api(apiUrl)
            const { data } = await res.json()
            return data[responseKey] || []
        } catch {
            return []
        }
    }

    // ======================================================================================
    // 상세페이지 열기
    // ======================================================================================
    function setContent(id: number | undefined) {

        if(id == undefined) {
            return;
        }

        if(isTest) console.log("**** setContent :: ", id);

        const params = new URLSearchParams(searchParams.toString())
        if (id) params.set("id", String(id))
        else params.delete("id")

        safeReplace(params)

        let item = cachedContents[id]
        if (!item) {
            item = list.find(el => el.id === id)
            if (item) {
                setCachedContents(prev => ({ ...prev, [id]: item }))
            }
        }
        setSelectContent(item)

        requestAnimationFrame(() => {
            window.scrollTo({ top: 0 })
        })
    }


    // ======================================================================================
    // 요청된 id를 포함한 리스트 로드
    // ======================================================================================
    const [isScanning, setIsScanning] = useState(false)
    async function scanUntilFound(targetId: number) {
        setIsScanning(true)
        let currentPage = 1
        setLoading(true)

        while (true) {
            const beforeCount = list.length


            // 페이지 데이터 가져오기
            const data = await fetchListPageReturn(currentPage)
            if(isTest) console.log("**** scanUntilFound : ", data, data.length)

            // 더 이상 데이터가 없으면 종료
            if (!data.length) {
                setSelectContent(undefined)
                customAlert(a("InvalidPage"), "warning")
                setLoading(false)
                setIsScanning(false)
                return false
            }

            // 리스트에 누적 추가 (중복 제거)
            setList(prev => {
                const exist = new Set(prev.map(p => p.id))
                const filtered = data.filter((v: any) => !exist.has(v.id))
                return [...prev, ...filtered]
            })

            await Promise.resolve()
            await new Promise(res => setTimeout(res, 0))


            // 방금 가져온 페이지에서 id 찾기
            const found = data.find((el: any) => el.id === targetId)
            if (found) {
                setSelectContent(found)
                setLoading(false)
                setIsScanning(false)
                return true
            }

            // 다음 페이지로
            currentPage++
            if(isTest) console.log("**** 다음 페이지로 ", currentPage, data.length)

            // 혹시 무한루프 방지
            if (data.length === 0) {
            // if (beforeCount === list.length) {
                if(isTest) console.log("**** 혹시 무한루프 방지", beforeCount, list.length)

                // 데이터에 아무 변화가 없다면 종료
                setSelectContent(undefined)
                customAlert(a("InvalidPage"), "warning")
                setLoading(false)
                setIsScanning(false)
                return false
            }
        }
    }




    // ======================================================================================
    // 초기 페이지 설정 (탭 변경 시)
    // ======================================================================================
    useEffect(() => {
        if(isTest)  console.log( "**** : 초기 페이지 설정");
        const thisTab = tabs_path.find(el => el.path === pathName)
        if (!thisTab) return

        SetPageTitle(thisTab.name)
        setEditCategory(thisTab.param?.cate ?? defaultCate)

        setQueryString({
            ...searchQueryStringTypesDefault,
            page: 1,
            page_count: perPage,
            ...thisTab.param,
        })

        setList([])
        setHasMore(true)
        setPage(1)
        setIsInitialLoad(true)
        setIgnoreId(false)
        setSelectContent(undefined)
    }, [pathName])

    // ======================================================================================
    // queryString 변화 → 새 리스트 로딩
    // ======================================================================================
    useEffect(() => {
        if (!queryString) return
        if (isReplacingUrl) return
        if (isScanning) return 

        if(isTest) console.log("**** -------------여기부터-------------------");
        if(isTest) console.log("**** Change queryString : ", queryString)

        const idFromUrl = Number(searchParams.get("id"))

        // 초기 진입 special handling
        if (isInitialLoad) {
            ;(async () => {

                if (idFromUrl) {
                    // 전체 페이지 스캔하면서 해당 id를 찾는 모드
                    const found = await scanUntilFound(idFromUrl)
                    
                    setIgnoreId(true)
                    setIsInitialLoad(false)
                    return
                }

                // id 없으면 첫 페이지만 로드
                await fetchListPage(1)
                setIgnoreId(true)
                setIsInitialLoad(false)
            })()
            return
        }

        // === 초기 이후 ===
        if (!ignoreId && idFromUrl) return

        // 검색/탭 이동으로 queryString이 바뀐 경우
        setList([])
        setHasMore(true)
        setPage(1)
        fetchListPage(1)

    }, [queryString])

    useEffect(() => {
        const idFromUrl = Number(searchParams.get("id"))

        // 뒤로가기 시 매개변수 사라지면 상세컨텐츠 종료
        if(!idFromUrl){
            setContent(undefined)
            setSelectContent(undefined)
        }else{
            // if (!isInitialLoad) scanUntilFound(idFromUrl)

            // 매개변수 존재하는 url초기진입 시 작동하는 로직이 존재하기때문에 초기진입 제외
            if (!isInitialLoad) {

                // 빈 리스트면 초기로드 실행
                if (list.length === 0) {
                    fetchListPage(1)
                    return
                }
                // 이미 로드된 리스트에 있으면 스캔하지 않음 → 불필요 fetch 방지
                const exist = list.find(el => el.id === idFromUrl)
                if (exist) {
                    setSelectContent(exist)
                } else {
                    scanUntilFound(idFromUrl)
                }
            }
        }

        const reloadParam = Number(searchParams.get("_"))
        if(reloadParam){
            const params = new URLSearchParams(searchParams.toString());
            params.delete("_");
            const cleanUrl = pathName + (params.toString() ? `?${params.toString()}` : "");
            window.history.replaceState(null, "", cleanUrl);
        }
    }, [searchParams])


    


    // ======================================================================================
    // 무한스크롤 → page 증가 시 fetch
    // ======================================================================================
    useEffect(() => {
        if (isScanning) return 
        if (isInitialLoad) return
        if (!hasMore) return
        fetchListPage(page)
    }, [page])

    // ======================================================================================
    // Intersection Observer
    // ======================================================================================
    useEffect(() => {
        if (isScanning) return 
        if (!loadMoreRef.current) return
        if (!hasMore || loading) return
        if (isuserSearch) return

        if(isTest) console.log( "**** Intersection Observer ");

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    // if (entry.isIntersecting && !loading && page > 1 && !isScanning) {
                    if (entry.isIntersecting && !loading ) {
                        setPage(p => p + 1)
                    }
                })
            },
            { root: null, threshold: 0.1 }
        )

        observer.observe(loadMoreRef.current)
        return () => observer.disconnect()
    }, [isScanning, hasMore, loading])

    // ======================================================================================
    // 리스트 비었을 때 컨텐츠 제거 유지
    // ======================================================================================
    useEffect(() => {
        if (!list.length && !isInitialLoad) {
            setSelectContent(undefined)
        }
    }, [list])

    // ======================================================================================
    // 권한 계산
    // ======================================================================================
    useEffect(() => {
        if (!user?.is_authenticated) return
        setWriteAuth(editCategories.includes(editCategory))
        setEditAuth(selectContent?.is_author || user.is_admin)
    }, [user, editCategory, selectContent])
    useEffect(() => {
        reloadUntilCurrentPage(); // 로그인 , 로그아웃 시 페이지 리로드
    }, [user])

    // ======================================================================================
    // 배너 타입
    // ======================================================================================
    const [bannerTypeNumber, setBannerTypeNumber] = useState(76)
    useEffect(() => {
        const key = pageTitle.replace(/\s+/g, "")
        switch (key) {
            case "스폰업체":
            case "스폰랭킹":
            case "즐겨찾기":
                mobileMode ? setBannerTypeNumber(73) : setBannerTypeNumber(33)
                break
            case "스포츠분석":
            case "회원분석":
                mobileMode ? setBannerTypeNumber(71) : setBannerTypeNumber(31)
                break
            case "이벤트매치":
            case "이벤트":
                mobileMode ? setBannerTypeNumber(77) : setBannerTypeNumber(37)
                break
            case "공지사항":
                mobileMode ? setBannerTypeNumber(78) : setBannerTypeNumber(38)
                break
        }
    }, [pageTitle])

    const t = useTranslations("PageTitle");
    
    if (!queryString) return <div></div>

    return (
        <div>
            {typeof window !== "undefined" && (
                <PageBanner bannerMap={page_top_banner.get(bannerTypeNumber)} />
            )}

            <div className="page-title">{t(pageTitle)}</div>
            <Tabs tabs_path={tabs_path} setContent={setContent}  />

            <CommonListContext.Provider
                value={{
                    list,
                    selectContent,
                    setContent,
                    editCategory,
                    isFirstRender,
                    setIsFirstRender,
                    post: apiUrlBase,
                    fetchData: reloadUntilCurrentPage,
                    loading,
                }}
            >
                {ViewComponent && (
                    <ViewComponent
                        selectContent={selectContent}
                        setSelectContent={setSelectContent}
                        post={apiUrlBase}
                        writeAuth={writeAuth}
                        editAuth={editAuth}
                        fetchData={reloadUntilCurrentPage}
                        isFirstRender={isFirstRender}
                        setIsFirstRender={setIsFirstRender}
                    />
                )}

                {isSearch && (
                    <Search
                        totalCount={totalCount}
                        queryString={queryString}
                        setQueryString={setQueryString}
                        setIsuserSearch={setIsuserSearch}
                        addQuery={
                            tabs_path.find((el) => el.path === pathName)?.param || {}
                        }
                    />
                )}

                {children}

                <div ref={loadMoreRef}></div>
            </CommonListContext.Provider>
        </div>
    )
}
